import ConfirmationModal from '@medical/components/ConfirmationModal'
import { ErrorMessage, Formik } from 'formik'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { Checkbox } from 'antd'
import { DatePicker } from '@medical/components/MomentPickers'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import UploadIdentity from '../DoctorIdentityForm/UploadIdentity'
import { DOWNLOAD_FILE } from '@medical/pages/staff/Doctors/DoctorDetail/DoctorDetail.graphql'
import { useMutation } from '@apollo/client'
import locale from 'antd/es/date-picker/locale/ja_JP'
import moment from 'moment/moment'

const MyCarCommutingApplicationForm = ({
  closeModal,
  onSubmit,
  confirmationMessage,
  disabled,
  initialValues,
}) => {
  const { i18n } = useTranslation()
  const [downloadFile] = useMutation(DOWNLOAD_FILE)

  const validationSchema = Yup.object({
    frontSideLicenseKey: Yup.mixed().required(
      i18n.t('validation.myCarCommutingApplication.frontSideLicenseRequired')
    ),
    backSideLicenseKey: Yup.mixed().required(
      i18n.t('validation.myCarCommutingApplication.backSideLicenseRequired')
    ),
    expirationDate: Yup.date()
      .nullable()
      .required(
        i18n.t('validation.myCarCommutingApplication.expirationDateRequired')
      )
      .typeError(
        i18n.t('validation.myCarCommutingApplication.expirationDateRequired')
      )
      .min(
        new Date(),
        i18n.t('validation.myCarCommutingApplication.expirationDateFuture')
      ),
    liabilityInsuranceCertificateExpirationDate: Yup.date()
      .nullable()
      .required(
        i18n.t('validation.myCarCommutingApplication.liabilityInsuranceCertificateExpirationDateRequired')
      )
      .typeError(
        i18n.t('validation.myCarCommutingApplication.liabilityInsuranceCertificateExpirationDateRequired')
      ).min(
        new Date(),
        i18n.t('validation.myCarCommutingApplication.liabilityInsuranceCertificateExpirationDateFuture')
      ),
    voluntaryInsuranceCertificateExpirationDate: Yup.date()
      .nullable()
      .required(
        i18n.t('validation.myCarCommutingApplication.voluntaryInsuranceCertificateExpirationDateRequired')
      )
      .typeError(
        i18n.t('validation.myCarCommutingApplication.voluntaryInsuranceCertificateExpirationDateRequired')
      ).min(
        new Date(),
        i18n.t('validation.myCarCommutingApplication.voluntaryInsuranceCertificateExpirationDateFuture')
      ),
    carCommuteStartDate: Yup.date()
      .nullable()
      .required(
        i18n.t('validation.myCarCommutingApplication.carCommuteStartDateRequired')
      )
      .typeError(
        i18n.t('validation.myCarCommutingApplication.carCommuteStartDateRequired')
      ),
    vehicleInspectionCertificateKey: Yup.mixed().required(
      i18n.t(
        'validation.myCarCommutingApplication.vehicleInspectionCertificateRequired'
      )
    ),
    liabilityInsuranceCertificateKey: Yup.mixed().required(
      i18n.t(
        'validation.myCarCommutingApplication.liabilityInsuranceCertificateRequired'
      )
    ),
    voluntaryInsuranceCertificateKey: Yup.mixed().required(
      i18n.t(
        'validation.myCarCommutingApplication.voluntaryInsuranceCertificateRequired'
      )
    ),
    vehicleInspectionExpirationDate: Yup.date()
      .nullable()
      .required(
        i18n.t(
          'validation.myCarCommutingApplication.vehicleInspectionExpirationDateRequired'
        )
      )
      .typeError(
        i18n.t(
          'validation.myCarCommutingApplication.vehicleInspectionExpirationDateRequired'
        )
      )
      .min(
        new Date(),
        i18n.t(
          'validation.myCarCommutingApplication.vehicleInspectionExpirationDateFuture'
        )
      ),
    vehicleType: Yup.string().required(
      i18n.t('validation.myCarCommutingApplication.vehicleTypeRequired')
    ),
    licensePlateNumberPart1: Yup.string(),
    licensePlateNumberPart2: Yup.string(),
    licensePlateNumberPart3: Yup.string(),
    licensePlateNumberPart4: Yup.string(),
    isAgreedToRegulations: Yup.boolean()
      .required(
        i18n.t(
          'validation.myCarCommutingApplication.isAgreedToRegulationsRequired'
        )
      )
      .oneOf(
        [true],
        i18n.t(
          'validation.myCarCommutingApplication.isAgreedToRegulationsRequired'
        )
      ),
  }).test('vehicle-number', function (values) {
    const {
      licensePlateNumberPart1,
      licensePlateNumberPart2,
      licensePlateNumberPart3,
      licensePlateNumberPart4,
    } = values || {}

    const licensePlateNumberPart1Value = licensePlateNumberPart1 || '';
    const licensePlateNumberPart2Value = licensePlateNumberPart2 || '';
    const licensePlateNumberPart3Value = licensePlateNumberPart3 || '';
    const licensePlateNumberPart4Value = licensePlateNumberPart4 || '';

    if (
      licensePlateNumberPart1Value.trim().length === 0 &&
      licensePlateNumberPart2Value.trim().length === 0 &&
      licensePlateNumberPart3Value.trim().length === 0 &&
      licensePlateNumberPart4Value.trim().length === 0) {
      return this.createError({
        path: 'vehicleNumber',
        message: i18n.t('validation.myCarCommutingApplication.licensePlateNumberRequired'),
      });
    }

    if (
      !/^[^\s　]+$/.test(licensePlateNumberPart1Value) ||
      !/^[A-Za-z0-9]{3}$/.test(licensePlateNumberPart2Value) ||
      !/^[ぁ-ん]$/.test(licensePlateNumberPart3Value) ||
      !/^\d{1,4}$/.test(licensePlateNumberPart4Value)
    ){
      return this.createError({
        path: 'vehicleNumber',
        message: i18n.t(
          'validation.myCarCommutingApplication.licensePlateNumberFormat'
        ),
      })
    }

    return true;
  });

  const downloadFileSubmit = async (key, callback) => {
    if (!key || key.length === 0) return
    try {
      const {
        data: { downloadFileS3: fileURL },
      } = await downloadFile({
        variables: {
          key,
        },
      })
      callback(fileURL)
    } catch (error) {
      callback(null)
    }
  }

  const defaultInitialValues = {
    frontSideLicenseKey: '',
    backSideLicenseKey: '',
    expirationDate: null,
    vehicleInspectionCertificateKey: '',
    liabilityInsuranceCertificateKey: '',
    voluntaryInsuranceCertificateKey: '',
    vehicleInspectionExpirationDate: null,
    vehicleType: '',
    isAgreedToRegulations: false,
    confirm: false,
    licensePlateNumberPart1: '',
    licensePlateNumberPart2: '',
    licensePlateNumberPart3: '',
    licensePlateNumberPart4: '',
    staffComment: '',
    carCommuteStartDate: null,
    liabilityInsuranceCertificateExpirationDate: null,
    voluntaryInsuranceCertificateExpirationDate: null,
  }

  return (
    <Formik
      initialValues={initialValues || defaultInitialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={onSubmit}
      render={formikProps => {
        const {
          values,
          setFieldValue,
          isSubmitting,
          validateForm,
          setTouched,
        } = formikProps

        function disabledDate(current) {
          return (
            current &&
            current <
            moment()
              .subtract(1, 'days')
              .endOf('day')
          )
        }
        return (
          <div
            style={{
              maxWidth: 800,
              margin: '0 auto',
              boxSizing: 'border-box',
              padding: '0 8px',
            }}
          >
            {values.staffComment !== '' ? (
              <div
                style={{
                  color: '#db7093',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  padding: '0 15px',
                }}
              >
                【管理者コメント】{values.staffComment}
              </div>
            ) : null}

            <div className='tr'>
              <div className='th required'>
                マイカー通勤開始日
              </div>
              <div className='td' style={{ width: '100%' }}>
                <DatePicker
                  className='date-picker mx-2'
                  placeholder={i18n.t(
                    'doctor.myCarCommutingApplication.selectDate'
                  )}
                  locale={locale}
                  value={
                    values.carCommuteStartDate ? moment(values.carCommuteStartDate) : null
                  }
                  onChange={e => {
                    setFieldValue('carCommuteStartDate', e ? e.toDate() : null)
                  }}
                  disabled={disabled}
                  style={{ width: '270px' }}
                  allowClear={false}
                />
                <ErrorMessage
                  name='carCommuteStartDate'
                  component='div'
                  className='signup-alert'
                />
              </div>
            </div>

            {/* ====== Driver License ====== */}
            <div className='tr'>
              <div className='th required'>
                {i18n.t('doctor.myCarCommutingApplication.driverLicensePhoto')}
              </div>
              <div className='td' style={{ width: '100%' }}>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 10,
                  }}
                >
                  <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                    <div style={{ marginBottom: 4 }}>
                      {i18n.t('doctor.myCarCommutingApplication.front')}
                    </div>
                    <UploadIdentity
                      i18n={i18n}
                      values={values}
                      fieldValue='frontSideLicenseKey'
                      setFieldValue={setFieldValue}
                      downloadFileSubmit={downloadFileSubmit}
                      imgKey={values.frontSideLicenseKey}
                    />
                    <ErrorMessage
                      name='frontSideLicenseKey'
                      component='div'
                      className='signup-alert'
                    />
                  </div>

                  <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                    <div style={{ marginBottom: 4 }}>
                      {i18n.t('doctor.myCarCommutingApplication.back')}
                    </div>
                    <UploadIdentity
                      i18n={i18n}
                      values={values}
                      fieldValue='backSideLicenseKey'
                      setFieldValue={setFieldValue}
                      downloadFileSubmit={downloadFileSubmit}
                      imgKey={values.backSideLicenseKey}
                    />
                    <ErrorMessage
                      name='backSideLicenseKey'
                      component='div'
                      className='signup-alert'
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Expiration Date */}
            <div className='tr'>
              <div className='th required'>
                {i18n.t('doctor.myCarCommutingApplication.expirationDate')}
              </div>
              <div className='td' style={{ width: '100%' }}>
                <DatePicker
                  className='date-picker mx-2'
                  placeholder={i18n.t(
                    'doctor.myCarCommutingApplication.selectDate'
                  )}
                  locale={locale}
                  value={
                    values.expirationDate ? moment(values.expirationDate) : null
                  }
                  onChange={e => {
                    setFieldValue('expirationDate', e ? e.toDate() : null)
                  }}
                  disabled={disabled}
                  disabledDate={disabledDate}
                  style={{ width: '270px' }}
                  allowClear={false}
                />
                <ErrorMessage
                  name='expirationDate'
                  component='div'
                  className='signup-alert'
                />
              </div>
            </div>

            {/* ====== Vehicle documents (2 columns, responsive) ====== */}
            <div className='tr'>
              <div className='th required'>
                {i18n.t(
                  'doctor.myCarCommutingApplication.vehicleInspectionCertificate'
                )}
              </div>
              <div className='td' style={{ width: '100%' }}>
                <UploadIdentity
                  i18n={i18n}
                  values={values}
                  fieldValue='vehicleInspectionCertificateKey'
                  setFieldValue={setFieldValue}
                  downloadFileSubmit={downloadFileSubmit}
                  imgKey={values.vehicleInspectionCertificateKey}
                />
                <ErrorMessage
                  name='vehicleInspectionCertificateKey'
                  component='div'
                  className='signup-alert'
                />
              </div>
            </div>

            {/* Vehicle Inspection Expiration Date */}
            <div className='tr'>
              <div className='th required'>
                {i18n.t(
                  'doctor.myCarCommutingApplication.vehicleInspectionExpirationDate'
                )}
              </div>
              <div className='td' style={{ width: '100%' }}>
                <DatePicker
                  className='date-picker mx-2'
                  placeholder={i18n.t(
                    'doctor.myCarCommutingApplication.selectDate'
                  )}
                  locale={locale}
                  value={
                    values.vehicleInspectionExpirationDate
                      ? moment(values.vehicleInspectionExpirationDate)
                      : null
                  }
                  onChange={e => {
                    setFieldValue(
                      'vehicleInspectionExpirationDate',
                      e ? e.toDate() : null
                    )
                  }}
                  disabled={disabled}
                  disabledDate={disabledDate}
                  style={{ width: '305px' }}
                  allowClear={false}
                />
                <ErrorMessage
                  name='vehicleInspectionExpirationDate'
                  component='div'
                  className='signup-alert'
                />
              </div>
            </div>

            <div className='tr'>
              <div className='th required'>
                {i18n.t(
                  'doctor.myCarCommutingApplication.liabilityInsuranceCertificate'
                )}
              </div>
              <div className='td' style={{ width: '100%' }}>
                <UploadIdentity
                  i18n={i18n}
                  values={values}
                  fieldValue='liabilityInsuranceCertificateKey'
                  setFieldValue={setFieldValue}
                  downloadFileSubmit={downloadFileSubmit}
                  imgKey={values.liabilityInsuranceCertificateKey}
                />
                <ErrorMessage
                  name='liabilityInsuranceCertificateKey'
                  component='div'
                  className='signup-alert'
                />
              </div>
            </div>

            <div className='tr'>
              <div className='th required'>
                自賠責保険有効期限
              </div>
              <div className='td' style={{ width: '100%' }}>
                <DatePicker
                  className='date-picker mx-2'
                  placeholder={i18n.t(
                    'doctor.myCarCommutingApplication.selectDate'
                  )}
                  locale={locale}
                  value={
                    values.liabilityInsuranceCertificateExpirationDate ? moment(values.liabilityInsuranceCertificateExpirationDate) : null
                  }
                  onChange={e => {
                    setFieldValue('liabilityInsuranceCertificateExpirationDate', e ? e.toDate() : null)
                  }}
                  disabled={disabled}
                  disabledDate={disabledDate}
                  style={{ width: '270px' }}
                  allowClear={false}
                />
                <ErrorMessage
                  name='liabilityInsuranceCertificateExpirationDate'
                  component='div'
                  className='signup-alert'
                />
              </div>
            </div>

            {/* ====== Vehicle Info ====== */}
            <div className='tr'>
              <div className='th required'>
                {i18n.t('doctor.myCarCommutingApplication.vehicleType')}
              </div>
              <div className='td' style={{ width: '100%' }}>
                <InputText
                  value={values.vehicleType || ''}
                  onChange={e => setFieldValue('vehicleType', e.target.value)}
                  placeholder={i18n.t(
                    'doctor.myCarCommutingApplication.vehicleTypePlaceholder'
                  )}
                  disabled={disabled}
                  style={{ width: '338px' }}
                />
                <ErrorMessage
                  name='vehicleType'
                  component='div'
                  className='signup-alert'
                />
              </div>
            </div>

            <div className='tr'>
              <div className='th required'>
                {i18n.t('doctor.myCarCommutingApplication.licensePlateNumber')}
              </div>
              <div
                className='td'
                style={{
                  width: '100%',
                  display: 'flex',
                  gap: '5px',
                  flexWrap: 'wrap',
                }}
              >
                <InputText
                  value={values.licensePlateNumberPart1 || ''}
                  onChange={e =>
                    setFieldValue('licensePlateNumberPart1', e.target.value)
                  }
                  placeholder={'例：東京'}
                  disabled={disabled}
                  style={{ width: '80px' }}
                />

                <InputText
                  value={values.licensePlateNumberPart2 || ''}
                  onChange={e =>
                    setFieldValue('licensePlateNumberPart2', e.target.value)
                  }
                  placeholder={'例：30X'}
                  disabled={disabled}
                  style={{ width: '80px' }}
                />

                <InputText
                  value={values.licensePlateNumberPart3 || ''}
                  onChange={e =>
                    setFieldValue('licensePlateNumberPart3', e.target.value)
                  }
                  placeholder={'例：あ'}
                  disabled={disabled}
                  style={{ width: '80px' }}
                />

                <InputText
                  value={values.licensePlateNumberPart4 || ''}
                  onChange={e =>
                    setFieldValue('licensePlateNumberPart4', e.target.value)
                  }
                  placeholder={'例：22'}
                  disabled={disabled}
                  style={{ width: '80px' }}
                />
                <ErrorMessage
                  name='vehicleNumber'
                  component='div'
                  style={{ width: '100%' }}
                  className='signup-alert'
                />
              </div>
            </div>

            {/* ====== Voluntary insurance certificate ====== */}
            <div className='tr'>
              <div className='th required'>任意保険加入証明書</div>
              <div className='td' style={{ width: '100%' }}>
                <UploadIdentity
                  i18n={i18n}
                  values={values}
                  fieldValue='voluntaryInsuranceCertificateKey'
                  setFieldValue={setFieldValue}
                  downloadFileSubmit={downloadFileSubmit}
                  imgKey={values.voluntaryInsuranceCertificateKey}
                />
                <ErrorMessage
                  name='voluntaryInsuranceCertificateKey'
                  component='div'
                  className='signup-alert'
                />
              </div>

              <div className='tr'>
                <div className='th required'>
                  任意保険有効期限
                </div>
                <div className='td' style={{ width: '100%' }}>
                  <DatePicker
                    className='date-picker mx-2'
                    placeholder={i18n.t(
                      'doctor.myCarCommutingApplication.selectDate'
                    )}
                    locale={locale}
                    value={
                      values.voluntaryInsuranceCertificateExpirationDate ? moment(values.voluntaryInsuranceCertificateExpirationDate) : null
                    }
                    onChange={e => {
                      setFieldValue('voluntaryInsuranceCertificateExpirationDate', e ? e.toDate() : null)
                    }}
                    disabled={disabled}
                    disabledDate={disabledDate}
                    style={{ width: '270px' }}
                    allowClear={false}
                  />
                  <ErrorMessage
                    name='voluntaryInsuranceCertificateExpirationDate'
                    component='div'
                    className='signup-alert'
                  />
                </div>
              </div>

              <div
                className='td'
                style={{
                  width: '100%',
                  textAlign: 'right',
                  paddingTop: 0,
                  fontSize: '12px',
                }}
              >
                申請者が適用されるか分かるものを添付
              </div>
            </div>

            {/* ====== Regulations ====== */}
            <div
              style={{
                margin: '32px 0 !important',
                maxWidth: '100%',
                boxSizing: 'border-box',
              }}
              className='course-description-doctor'
            >
              <p style={{ textAlign: 'center' }}>
                <b>マイカー規定について</b>
              </p>
              <div>
                <p style={{ marginBottom: '0' }}>＜マイカー通勤におけるご申請のお願い＞</p>
                <p>
                  マイカー通勤を希望される場合は、利用開始前までにマイカー通勤申請が必要です。
                  基本的には公共交通機関による通勤をお願いしておりますが、マイカー通勤をご希望の際は、以下マイカー通勤申請規程をご確認いただき、許可基準を満たしていることをご確認のうえ、申請をお願いいたします。
                </p>
                <p style={{ marginBottom: '0', marginTop:'10px' }}>▼マイカー通勤申請規程</p>
                <a href='https://drive.google.com/file/d/1xKk9EFIqdjDpAosAfdZ9qVWVanazma8E/view?usp=sharing'>
                  https://drive.google.com/file/d/1xKk9EFIqdjDpAosAfdZ9qVWVanazma8E/view?usp=sharing
                </a>
              </div>

              <div style={{ marginTop:'10px' }}>
                <p style={{ marginBottom: '0' }}>＜マイカー通勤における同意＞</p>
                <p>
                  道路交通法その他自動車交通関係法令ならびにマイカー通勤に関する規程を遵守し、<br />
                  常に安全運転に努めることにご同意いただける場合は、規程への同意欄にチェックを入れてください。
                  <br />※ご同意いただけない場合は、マイカー通勤の許可はできませんのでご了承ください。
                </p>
              </div>

              <div style={{ marginTop:'10px' }}>
                <p style={{ marginBottom: '0' }}>＜マイカー通勤時の費用精算＞</p>
                <p>
                  マイカー通勤時の経路距離は［ご自宅～勤務先クリニック］までのルート経路距離に基づくガソリン代を、当社規程に基づき往復分にてお支払いいたします。<br />
                  通勤経路は、ご登録いただいた住所から勤務先クリニックまでの最短経路を自動算出いたします。<br />
                  ※算出された経路距離は、「交通費申請」→「マイカー通勤時の経路距離について」よりご確認いただけます。
                </p>
                <p>
                  実際の通行可能な経路と異なるなど、経路距離の変更が必要な場合は、お手数ですがドクターサポート事務局までお問い合わせください。<br />
                  また、ご通勤に伴う高速道路代や駐車場代につきましては、「その他交通費精算」よりご申請ください。
                </p>
              </div>

              <div className='row' style={{ alignItems: 'flex-start' }}>
                <Checkbox
                  checked={values.isAgreedToRegulations}
                  onChange={e =>
                    setFieldValue('isAgreedToRegulations', e.target.checked)
                  }
                  disabled={disabled}
                />
                <p style={{ marginLeft: 10, marginBottom: 0 }}>
                  {i18n.t(
                    'doctor.myCarCommutingApplication.agreeToRegulations'
                  )}
                </p>
              </div>
              <div className='row'>
                <ErrorMessage
                  name='isAgreedToRegulations'
                  component='div'
                  className='signup-alert'
                />
              </div>
            </div>

            {/* Buttons */}
            <div className='modal-bottom' style={{ marginTop: 24 }}>
              <Button
                className='cancel-button'
                onClick={closeModal}
                disabled={isSubmitting}
                label={i18n.t('doctor.myCarCommutingApplication.cancelButton')}
              />
              <Button
                className='reflect-button'
                onClick={() => {
                  validateForm().then(errors => {
                    if (!Object.keys(errors).length) {
                      setFieldValue('confirm', true)
                    } else {
                      // Đánh dấu tất cả field có lỗi là touched
                      const touched = Object.keys(errors).reduce(
                        (acc, key) => ({ ...acc, [key]: true }),
                        {}
                      )
                      setTouched(touched)
                    }
                  })
                }}
                disabled={isSubmitting || disabled}
                label={i18n.t('doctor.myCarCommutingApplication.submitButton')}
              />
            </div>

            <ConfirmationModal
              {...formikProps}
              confirmationMessage={confirmationMessage}
            />
          </div>
        )
      }}
    />
  )
}

export default MyCarCommutingApplicationForm
